import React, { createContext, ReactNode, useContext } from 'react';

import { SelectChangeEvent, Switch } from '@mui/material';
import cn from 'classnames';

import Button from '@components/Button';
import SeuratDifferentialGroupsPickerFields from '@components/experiments/analyses/fields/SeuratDifferentialGroupsPickerFields';
import SeuratDifferentialLatentVariablesField from '@components/experiments/analyses/fields/SeuratDifferentialLatentVariablesField';
import SeuratDifferentialVariablesField from '@components/experiments/analyses/fields/SeuratDifferentialVariablesField';
import { useSwitchStyles } from '@components/SwitchStyles';

import { ExperimentAnalysis } from '@models/analysis/ExperimentAnalysis';
import { SeuratDifferentialExpressionAnalysisParameters } from '@models/AnalysisParameters';
import AnnotationSet from '@models/Annotation';
import Experiment from '@models/Experiment';
import Plot from '@models/Plot';
import { PlotDisplayOption } from '@models/PlotDisplayOption';

import SimpleSelectField, { ChoiceItem } from './SimpleSelectField';

interface CellGroupsContextType<T> {
    analysisParameters: T;
    experiment: Experiment;
    plot: Plot<ExperimentAnalysis, PlotDisplayOption>;
    annotationSets: AnnotationSet[] | null;
    isCleanedWorkflow: boolean;
}
interface CellGroupsProps<T> extends CellGroupsContextType<T> {
    children: ReactNode;
}
interface HeaderProps {
    text?: string;
}
interface DescriptionProps {
    text?: string;
}
interface SubmitButtonProps {
    disabled?: boolean;
    onClick: () => void;
}
interface GroupByClusterFieldProps {
    checked: boolean;
    label?: string;
    name?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
interface AnnotationSetFieldProps {
    value: any;
    label?: string;
    name?: string;
    placeholder?: string;
    onChange: (e: SelectChangeEvent<any>) => void;
}

const CellGroupsContext = createContext<
    CellGroupsContextType<SeuratDifferentialExpressionAnalysisParameters> | undefined
>(undefined);

const useCellGroups = () => {
    const context = useContext(CellGroupsContext);
    if (context === undefined) {
        throw new Error('useCellGroups must be used within a CellGroups provider');
    }
    return context;
};

const CellGroups: React.FC<CellGroupsProps<SeuratDifferentialExpressionAnalysisParameters>> & {
    AnnotationSetField: React.FC<AnnotationSetFieldProps>;
    Description: React.FC<DescriptionProps>;
    GroupByClusterField: React.FC<GroupByClusterFieldProps>;
    GroupsPickerFields: React.FC;
    Header: React.FC<HeaderProps>;
    LatentVariablesField: React.FC;
    SubmitButton: React.FC<SubmitButtonProps>;
    VariablesField: React.FC;
} = ({
    analysisParameters,
    annotationSets,
    children,
    experiment,
    isCleanedWorkflow,
    plot,
}: CellGroupsProps<SeuratDifferentialExpressionAnalysisParameters>) => {
    const value = {
        analysisParameters,
        experiment,
        plot,
        annotationSets,
        isCleanedWorkflow,
    };

    return (
        <CellGroupsContext.Provider value={value}>
            <section className="mb-8 space-y-4">{children}</section>
        </CellGroupsContext.Provider>
    );
};

const Description: React.FC<DescriptionProps> = ({
    text = 'Group your cells by combining cluster annotation sets, variables, and/or latent variables',
}) => {
    return <p className="mb-4">{text}</p>;
};

const Header: React.FC<HeaderProps> = ({ text = 'Cell groups' }) => {
    return <h4 className="text-dark mb-2 text-lg font-semibold tracking-tight">{text}</h4>;
};

const LatentVariablesField: React.FC = () => {
    const { experiment, plot, analysisParameters } = useCellGroups();
    return (
        <SeuratDifferentialLatentVariablesField
            plot={plot}
            analysisParameters={analysisParameters}
            experiment={experiment}
        />
    );
};

const SubmitButton: React.FC<SubmitButtonProps> = ({ disabled, onClick }) => {
    return (
        <Button variant="contained" color="primary" onClick={onClick} disabled={disabled}>
            Update groups
        </Button>
    );
};

const VariablesField: React.FC = () => {
    const { experiment, plot, analysisParameters } = useCellGroups();
    return (
        <SeuratDifferentialVariablesField plot={plot} analysisParameters={analysisParameters} experiment={experiment} />
    );
};

const GroupByClusterField: React.FC<GroupByClusterFieldProps> = ({
    checked,
    onChange,
    name = 'split_by_cluster',
    label = 'Group by cluster',
}) => {
    const switchStyles = useSwitchStyles();
    return (
        <div className="form-field !flex flex-row items-center justify-between">
            <span className="field-label">{label}</span>
            <Switch sx={switchStyles} checked={checked} name={name} onChange={onChange} />
        </div>
    );
};

const AnnotationSetField: React.FC<AnnotationSetFieldProps> = ({
    value,
    label = 'Cluster annotation set',
    name = 'annotation_set_id',
    placeholder = 'Select an annotation set...',
    onChange,
}) => {
    const { annotationSets, isCleanedWorkflow } = useCellGroups();
    const ClusterAnnotationSetItems: ChoiceItem[] =
        annotationSets
            ?.sort((a, b) => a.resolution - b.resolution)
            .map((c) => {
                return {
                    label: (
                        <div className="flex flex-col">
                            <p className="text-dark">{c.display_name}</p>
                            <div className="flex flex-row text-sm text-gray-500">
                                {isCleanedWorkflow ? null : (
                                    <>
                                        <p>Resolution: {c.resolution.toFixed(1)}</p>{' '}
                                    </>
                                )}
                                <p
                                    className={cn({
                                        'ml-4': !isCleanedWorkflow,
                                    })}
                                >
                                    Clusters: {c.clusters.length}
                                </p>
                            </div>
                        </div>
                    ),
                    value: c.uuid,
                };
            }) ?? [];
    return (
        <SimpleSelectField
            name={name}
            label={label}
            // @Post-MVP: Adding expand button and modal
            // label={
            //     <>
            //         <span>{label}</span>
            //         <Button
            //             variant="text"
            //             color="primary"
            //             onClick={() => setExpanded(true)}
            //             endIcon={<ArrowsExpandIcon className="h-4 w-4" />}
            //         >
            //             Expand
            //         </Button>
            //     </>
            // }
            // labelClassName="w-full flex items-center justify-between"
            value={value}
            onChange={onChange}
            items={ClusterAnnotationSetItems}
            placeholder={placeholder}
        />
    );
};

const GroupsPickerFields = () => {
    const { experiment, plot } = useCellGroups();
    return <SeuratDifferentialGroupsPickerFields experiment={experiment} plot={plot} />;
};

CellGroups.AnnotationSetField = AnnotationSetField;
CellGroups.Description = Description;
CellGroups.GroupByClusterField = GroupByClusterField;
CellGroups.Header = Header;
CellGroups.LatentVariablesField = LatentVariablesField;
CellGroups.SubmitButton = SubmitButton;
CellGroups.VariablesField = VariablesField;
CellGroups.GroupsPickerFields = GroupsPickerFields;

export default CellGroups;
